import { useState } from "react";

import { ErrorIcon } from "react-hot-toast";
import ReactTooltip from "react-tooltip";
import styled from "styled-components";

import CopyIcon from "~/components/icons/Copy";
import { formatterDateTime } from "~/utils/formatterDateTime";

type IntegrationResultProps = {
  shortLabel?: string;
  longLabel: string;
  provider: string;
  value: string;
  date: Date;
  isHighAlert: boolean;
  logo: string;
  caseId?: string;
  linkUrl?: string;
  error?: any;
};

const ResultContainer = styled.div<{ isHighAlert?: boolean; isLink?: boolean }>`
  display: flex;
  flex-direction: column;
  border: 1px solid #787878;
  border-radius: 4px;
  padding: 6px 8px;
  ${(params) => params.isHighAlert && `color: #FF3A26;`}
  ${(params) => params.isHighAlert && `border-color: #FF3A26;`}
  ${(params) => params.isHighAlert && `background-color: #FFEBE9;`}
  ${(params) => params.isLink && `&:hover { span { text-decoration: underline; } }`}
  cursor: ${(params) => (params.isLink ? `pointer` : `default`)}
`;

const ResultRowContainer = styled.div`
  display: flex;
  align-items: center;
  column-gap: 6px;
  font-size: 9px;
  font-weight: 800;
`;
const CaseIdContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 10px;
`;

const CaseIdText = styled.text`
  font-family: Montserrat;
  font-size: 11px;
  font-weight: 400;
  line-height: 13.41px;
  text-align: left;
  color: rgba(0, 0, 0, 1);
  margin-right: 10px;
`;

const StyledCopyButton = styled.button`
  width: 22px;
  height: 22px;
  font-size: 9px;
  font-weight: 800;
  border: 1px solid #1f1f1f;
  border-radius: 4px;
  padding: 3px 4px;
  flex-shrink: 0;
  transition: background-color 0.3s ease;
  background-color: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;

  svg {
    transition: opacity 0.3s ease, transform 0.3s ease;
  }

  &::after {
    content: "✓";
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: opacity 0.3s ease;
    color: #babcba;
  }

  &:active {
    background-color: #e6e6e6;
  }

  &.copied {
    svg {
      opacity: 0;
      transform: scale(0.5);
    }

    &::after {
      opacity: 1;
    }
  }
`;

const HorizontalSeparator = styled.div<{ isHighAlert?: boolean }>`
  border-left: 1px solid #d3d3d3;
  height: 17px;
  ${(params) => params.isHighAlert && `border-color: #FF3A26;`}
`;

const OpaqueTooltip = styled(ReactTooltip).attrs(() => ({
  place: "bottom",
  effect: "solid",
  border: true,
  borderColor: "lightgray",
  backgroundColor: "#FFF",
  textColor: "#000",
  offset: { right: "3px" },
}))`
  opacity: 1 !important;
  padding-left: 13px !important;
`;

const TooltipContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 1px;
  max-width: 300px;
  font-size: 11px;
`;

const TooltipTitle = styled.div`
  font-size: 16px;
  font-weight: 800;
  text-transform: uppercase;
`;

const TooltipSubtitle = styled.div`
  font-size: 11px;
  font-weight: 700;
`;

const TooltipFooterText = styled.div<{ isHighAlert?: boolean }>`
  font-size: 12px;
  font-weight: 800;
  margin-top: 4px;
  ${(params) => params.isHighAlert && `color: #FF3A26;`}
`;

const LogoImg = styled.img`
  height: 15px;
  width: 15px;
`;

export default function IntegrationResult({
  shortLabel,
  longLabel,
  provider,
  value,
  date,
  isHighAlert,
  logo,
  linkUrl,
  caseId,
  error,
}: IntegrationResultProps) {
  const [isCopied, setIsCopied] = useState(false);

  const copyToClipboard = () => {
    if (caseId) {
      navigator.clipboard.writeText(caseId).then(() => {
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 2000);
      });
    }
  };

  const goToUrl = () => {
    if (linkUrl) {
      window.open(linkUrl, "_blank");
    }
  };

  if (error) {
    return (
      <p style={{ textDecoration: "none", cursor: "default", display: "flex", alignItems: "center" }}>
        Bad request
        <ErrorIcon className="ml-2" />
      </p>
    );
  }

  return (
    <>
      <ResultContainer data-tip data-for={provider} isHighAlert={isHighAlert} isLink={!!linkUrl}>
        <ResultRowContainer>
          <LogoImg src={logo} />
          <HorizontalSeparator isHighAlert={isHighAlert} />
          <span onClick={goToUrl}>{shortLabel ? `${shortLabel} - ${value}` : value}</span>
        </ResultRowContainer>
        {caseId && (
          <CaseIdContainer>
            <CaseIdText>{caseId}</CaseIdText>
            <StyledCopyButton onClick={copyToClipboard} className={isCopied ? "copied" : ""} disabled={isCopied}>
              <CopyIcon />
            </StyledCopyButton>
          </CaseIdContainer>
        )}
      </ResultContainer>
      <OpaqueTooltip id={provider}>
        <TooltipContentContainer>
          <TooltipTitle>{provider}</TooltipTitle>
          <TooltipSubtitle>{`Results when transfer was created:`}</TooltipSubtitle>
          <div>{formatterDateTime.format(new Date(date))}</div>
          <TooltipFooterText isHighAlert={isHighAlert}>{`${longLabel} - ${value}`}</TooltipFooterText>
        </TooltipContentContainer>
      </OpaqueTooltip>
    </>
  );
}
